import { IFsMap } from '@lib/geo/core';
import { SnLonLat } from '@lib/geo/helpers';

import { IMarker } from './marker.interface';

// ============================================================================
// EVENT TYPE GROUPINGS
// ============================================================================

// Group event types by their shared data types
type InteractionEventTypes =
  | 'click'
  | 'doubleclick'
  | 'rightclick'
  | 'mouseover'
  | 'mouseout'
  | 'mouseenter'
  | 'mouseleave';
type DragEventTypes = 'dragstart' | 'drag' | 'dragend';
type StateChangeEventTypes =
  | 'positionchange'
  | 'titlechange'
  | 'visibilitychange'
  | 'selectionchange'
  | 'iconchange'
  | 'stylechange';
type LifecycleEventTypes = 'add' | 'remove' | 'destroy';

// Combined event type (maintains backward compatibility)
export type MarkerEventType =
  | InteractionEventTypes
  | DragEventTypes
  | StateChangeEventTypes
  | LifecycleEventTypes;

// ============================================================================
// BASE INTERFACES
// ============================================================================

// Base event interface
export interface IMarkerEvent<
  I extends IFsMap,
  T extends MarkerEventType = MarkerEventType,
  M = IMarker<I>,
> {
  type: T;
  marker: M;
  timestamp: number;
}

// Interaction event data
export interface IMarkerInteractionData {
  event: MouseEvent | PointerEvent | TouchEvent;
  coordinates: {
    lat: number;
    lng: number;
  };
  button?: 'left' | 'right';
}

// Drag event data
export interface IMarkerDragData {
  position: SnLonLat;
  previousPosition?: SnLonLat;
  delta: {
    lat: number;
    lng: number;
  };
}

// State change event data
export interface IMarkerStateChangeData<V = unknown> {
  current: V;
  previous: V;
  changed: boolean;
}

// Lifecycle event data
export interface IMarkerLifecycleData {
  reason?: 'user' | 'programmatic' | 'system';
}

// Generic marker event with data
export interface IMarkerEventWithData<
  I extends IFsMap,
  T extends MarkerEventType,
  M = IMarker<I>,
  D = unknown,
> extends IMarkerEvent<I, T, M> {
  data: D;
}

// ============================================================================
// GENERIC EVENT TYPE HELPERS
// ============================================================================

// Generic helper for interaction events
type InteractionEvent<
  I extends IFsMap,
  T extends InteractionEventTypes,
  M = IMarker<I>,
> = IMarkerEventWithData<I, T, M, IMarkerInteractionData>;

// Generic helper for drag events
type DragEvent<I extends IFsMap, T extends DragEventTypes, M = IMarker<I>> = IMarkerEventWithData<
  I,
  T,
  M,
  IMarkerDragData
>;

// Generic helper for lifecycle events
type LifecycleEvent<
  I extends IFsMap,
  T extends LifecycleEventTypes,
  M = IMarker<I>,
> = IMarkerEventWithData<I, T, M, IMarkerLifecycleData>;

// ============================================================================
// STATE CHANGE EVENT MAPPING
// ============================================================================

// Map event types to their value types
type StateChangeValueMap = {
  positionchange: SnLonLat;
  titlechange: string;
  visibilitychange: boolean;
  selectionchange: boolean;
  iconchange: string;
  stylechange: Record<string, unknown>;
};

// Generic state change event type
type StateChangeEventForType<
  I extends IFsMap,
  T extends keyof StateChangeValueMap,
  M = IMarker<I>,
> = IMarkerEventWithData<I, T, M, IMarkerStateChangeData<StateChangeValueMap[T]>>;

// ============================================================================
// EXPORTED EVENT TYPES - INTERACTION EVENTS
// ============================================================================

export type IMarkerClickEvent<I extends IFsMap, M = IMarker<I>> = InteractionEvent<I, 'click', M>;
export type IMarkerDoubleClickEvent<I extends IFsMap, M = IMarker<I>> = InteractionEvent<
  I,
  'doubleclick',
  M
>;
export type IMarkerRightClickEvent<I extends IFsMap, M = IMarker<I>> = InteractionEvent<
  I,
  'rightclick',
  M
>;
export type IMarkerMouseOverEvent<I extends IFsMap, M = IMarker<I>> = InteractionEvent<
  I,
  'mouseover',
  M
>;
export type IMarkerMouseOutEvent<I extends IFsMap, M = IMarker<I>> = InteractionEvent<
  I,
  'mouseout',
  M
>;
export type IMarkerMouseEnterEvent<I extends IFsMap, M = IMarker<I>> = InteractionEvent<
  I,
  'mouseenter',
  M
>;
export type IMarkerMouseLeaveEvent<I extends IFsMap, M = IMarker<I>> = InteractionEvent<
  I,
  'mouseleave',
  M
>;

// ============================================================================
// EXPORTED EVENT TYPES - DRAG EVENTS
// ============================================================================

export type IMarkerDragStartEvent<I extends IFsMap, M = IMarker<I>> = DragEvent<I, 'dragstart', M>;
export type IMarkerDragMoveEvent<I extends IFsMap, M = IMarker<I>> = DragEvent<I, 'drag', M>;
export type IMarkerDragEndEvent<I extends IFsMap, M = IMarker<I>> = DragEvent<I, 'dragend', M>;

// ============================================================================
// EXPORTED EVENT TYPES - STATE CHANGE EVENTS
// ============================================================================

export type IMarkerPositionChangeEvent<I extends IFsMap, M = IMarker<I>> = StateChangeEventForType<
  I,
  'positionchange',
  M
>;
export type IMarkerTitleChangeEvent<I extends IFsMap, M = IMarker<I>> = StateChangeEventForType<
  I,
  'titlechange',
  M
>;
export type IMarkerVisibilityChangeEvent<
  I extends IFsMap,
  M = IMarker<I>,
> = StateChangeEventForType<I, 'visibilitychange', M>;
export type IMarkerSelectionChangeEvent<I extends IFsMap, M = IMarker<I>> = StateChangeEventForType<
  I,
  'selectionchange',
  M
>;
export type IMarkerIconChangeEvent<I extends IFsMap, M = IMarker<I>> = StateChangeEventForType<
  I,
  'iconchange',
  M
>;
export type IMarkerStyleChangeEvent<I extends IFsMap, M = IMarker<I>> = StateChangeEventForType<
  I,
  'stylechange',
  M
>;

// ============================================================================
// EXPORTED EVENT TYPES - LIFECYCLE EVENTS
// ============================================================================

export type IMarkerAddEvent<I extends IFsMap, M = IMarker<I>> = LifecycleEvent<I, 'add', M>;
export type IMarkerRemoveEvent<I extends IFsMap, M = IMarker<I>> = LifecycleEvent<I, 'remove', M>;
export type IMarkerDestroyEvent<I extends IFsMap, M = IMarker<I>> = LifecycleEvent<I, 'destroy', M>;

// ============================================================================
// UNION TYPES AND HELPERS
// ============================================================================

// Create arrays of event type names for programmatic generation
const INTERACTION_EVENTS = [
  'click',
  'doubleclick',
  'rightclick',
  'mouseover',
  'mouseout',
  'mouseenter',
  'mouseleave',
] as const;
const DRAG_EVENTS = ['dragstart', 'drag', 'dragend'] as const;
const STATE_CHANGE_EVENTS = [
  'positionchange',
  'titlechange',
  'visibilitychange',
  'selectionchange',
  'iconchange',
  'stylechange',
] as const;
const LIFECYCLE_EVENTS = ['add', 'remove', 'destroy'] as const;

// Generate union type programmatically
export type AllEventTypes =
  | (typeof INTERACTION_EVENTS)[number]
  | (typeof DRAG_EVENTS)[number]
  | (typeof STATE_CHANGE_EVENTS)[number]
  | (typeof LIFECYCLE_EVENTS)[number];

// Union type for all marker events (simplified approach)
export type IMarkerEventUnion<I extends IFsMap> =
  | IMarkerClickEvent<I>
  | IMarkerDoubleClickEvent<I>
  | IMarkerRightClickEvent<I>
  | IMarkerMouseOverEvent<I>
  | IMarkerMouseOutEvent<I>
  | IMarkerMouseEnterEvent<I>
  | IMarkerMouseLeaveEvent<I>
  | IMarkerDragStartEvent<I>
  | IMarkerDragMoveEvent<I>
  | IMarkerDragEndEvent<I>
  | IMarkerPositionChangeEvent<I>
  | IMarkerTitleChangeEvent<I>
  | IMarkerVisibilityChangeEvent<I>
  | IMarkerSelectionChangeEvent<I>
  | IMarkerIconChangeEvent<I>
  | IMarkerStyleChangeEvent<I>
  | IMarkerAddEvent<I>
  | IMarkerRemoveEvent<I>
  | IMarkerDestroyEvent<I>;

// Helper type for creating custom marker events (maintains backward compatibility)
export type CreateMarkerEvent<
  I extends IFsMap,
  T extends MarkerEventType,
  M = IMarker<I>,
  D = unknown,
> = IMarkerEventWithData<I, T, M, D>;

// ============================================================================
// UTILITY EXPORTS (Optional - for advanced usage)
// ============================================================================

// Export event type constants for runtime usage
export const EVENT_TYPES = {
  INTERACTION: INTERACTION_EVENTS,
  DRAG: DRAG_EVENTS,
  STATE_CHANGE: STATE_CHANGE_EVENTS,
  LIFECYCLE: LIFECYCLE_EVENTS,
} as const;

// Helper to check if an event is of a specific category
export const isInteractionEvent = (
  eventType: MarkerEventType,
): eventType is InteractionEventTypes =>
  (INTERACTION_EVENTS as readonly string[]).includes(eventType);

export const isDragEvent = (eventType: MarkerEventType): eventType is DragEventTypes =>
  (DRAG_EVENTS as readonly string[]).includes(eventType);

export const isStateChangeEvent = (
  eventType: MarkerEventType,
): eventType is StateChangeEventTypes =>
  (STATE_CHANGE_EVENTS as readonly string[]).includes(eventType);

export const isLifecycleEvent = (eventType: MarkerEventType): eventType is LifecycleEventTypes =>
  (LIFECYCLE_EVENTS as readonly string[]).includes(eventType);
